import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Control, FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import moment from "moment-timezone";
import { twMerge } from "tailwind-merge";

import { CustomInput } from "../../../../atoms/CustomInput";
import { CustomButton } from "../../../../atoms/CustomButton";
import NoTranslate from "../../../../atoms/NoTranslate";
import TimeZoneSelector from "../TimeZoneSelector";
import { ScheduleField } from "./useScheduleField";

type FieldErrorLike =
  | FieldError
  | Merge<FieldError, FieldErrorsImpl<Record<string, unknown>>>
  | string;

interface ScheduleControlProps {
  schedule: ScheduleField;
  control: Control<any>;
  valueName: string;
  error?: FieldErrorLike;
  id?: string;
  className?: string;
}

const ScheduleControl: React.FC<ScheduleControlProps> = ({
  schedule,
  control,
  valueName,
  error,
  id,
  className,
}) => {
  const { t } = useTranslation();
  const [showTimeZonePicker, setShowTimeZonePicker] = useState(false);

  const { timezone, setTimezone, minDateTime, maxDateTime, value } = schedule;

  return (
    <div
      id={id}
      className={twMerge(
        "rounded-md border border-gray-200 bg-white p-3 text-left",
        className
      )}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1">
          <span className="block text-sm font-medium text-gray-700">
            {t("dashboard.entities.viewEntityUser.sendMsg.timeZone")}
          </span>
          {showTimeZonePicker ? (
            <NoTranslate>
              <TimeZoneSelector
                inputId={`${id ?? "schedule"}-timezone`}
                currentTimeZone={timezone}
                onChange={setTimezone}
              />
            </NoTranslate>
          ) : (
            <div className="space-y-0.5">
              <p className="text-sm text-gray-600">
                <NoTranslate>
                  {t("dashboard.entities.viewEntityUser.sendMsg.timesShownIn", {
                    timezone,
                  })}
                </NoTranslate>
              </p>
              <CustomButton
                variant="none"
                size="xs"
                onClick={() => setShowTimeZonePicker(true)}
                className="px-0 font-medium text-blue-700 underline enabled:hover:text-blue-800"
                title={
                  t(
                    "dashboard.entities.viewEntityUser.sendMsg.changeTimeZone"
                  ) as string
                }
              />
            </div>
          )}
        </div>

        <div className="space-y-1">
          <CustomInput
            name={valueName}
            control={control}
            type="datetime-local"
            label={
              t(
                "dashboard.entities.viewEntityUser.sendMsg.dateAndTime"
              ) as string
            }
            error={error}
            helperText={error}
            min={minDateTime}
            max={maxDateTime}
          />
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-500">
        {t("dashboard.entities.viewEntityUser.sendSms.scheduleDeliveryInfo")}
      </p>

      {value && !error && (
        <p className="mt-1 text-sm text-gray-600">
          {t(
            "dashboard.entities.viewEntityUser.sendMsg.summaryLabels.scheduledFor"
          )}{" "}
          <NoTranslate>
            {moment(value).format("lll")} ({timezone})
          </NoTranslate>
        </p>
      )}
    </div>
  );
};

export default ScheduleControl;
